import cx from 'classnames';
import { intcomma } from 'journalize';
import { Component, h } from 'preact';

import OtherCandidate from './OtherCandidate';
import { PreactHTMLConverter } from 'preact-html-converter';

// internal
import {
  alert_conditionals,
  alert_winners,
  text_conditionals,
  sort_candidates_by_party,
} from '../election-utils';
import sumValues from '../utils/sum-values';
import formatPercent from '../utils/format-percent';

import { isCanvass, visibleCandidatesCount, DDHQestimatedvotes } from '../endpoints';

type Props = {
  contest: { [key: string]: any };
  columnCount: number;
  contestTitle: string;
  includeCountyRundown?: boolean;
  isShowcaseRace?: boolean;
  renderParty: any;
  renderCandidate: any;
  candidatesRender: any;
  embedType: string;
  broadcastHeaderImage: string;
  activeContestId: string;
  partyName: string;
  tableHeader: JSX.Element;
  annotations: { [key: string]: any };
  notes: { [key: string]: any };
  isHomepage?: boolean;
  asOf: string;
  source: string;
};

class Contest extends Component<Props> {
  constructor() {
    super();

    this.state = {
      viewAllCandidates: false,
    };
    this.onViewableToggle = this.onViewableToggle.bind(this);
  }

  onViewableToggle() {
    this.setState(prevState => ({
      viewAllCandidates: !prevState.viewAllCandidates, // set viewAllCandidates to what is wasn't before
    }));
  }
  render() {
    const {
      contest,
      columnCount,
      contestTitle,
      isShowcaseRace,
      renderCandidate,
      partyName,
      annotations,
      notes,
      tableHeader,
      isHomepage,
      asOf,
      source,
      embedType,
      candidatesRender,
    } = this.props;

    const { viewAllCandidates } = this.state;

    const converter = PreactHTMLConverter();

    const shouldDisplayResult = contest.contest_result !== 'not called';

    let CandidatesShown = isHomepage ? 2 : visibleCandidatesCount

    // Get uncontested races that are not state races because we won't be receiving votes for those races

    // Uncontested are going to be called regardless of being marked as winners

    const uncontested = contest.candidacies.length == 1;

    const uncontestedNoHouse = contest.candidacies.length == 1 && contest.office !== 'U.S. House';

    // const uncontested = contest.candidacies.length == 1 && contest.office !== 'U.S. House';

    const totalVotes = sumValues(contest.candidacies, 'votes');

    let candidates;

    candidates =
      totalVotes == 0
        ? sort_candidates_by_party(contest.candidacies)
        : contest.candidacies;

    const pct_votes = formatPercent(totalVotes, contest[DDHQestimatedvotes]);
    const pct_votes_turnout =
      embedType == 'homepage'
        ? pct_votes < 100
          ? `An estimated ${pct_votes}% of votes have been counted.`
          : `An estimated >99% of votes have been counted.`
        : pct_votes < 100
        ? `An estimated ${pct_votes}% of votes have been counted, according to Decision Desk HQ turnout models.`
        : `An estimated >99% of votes have been counted, according to Decision Desk HQ turnout models.`;

    const pct_votes_turnout_w_uncontested =
       (uncontestedNoHouse == true)
          ? text_conditionals(pct_votes, contest.candidacies.length) :
          pct_votes_turnout

    const pct_votes_explain = !shouldDisplayResult && !uncontested
      ? text_conditionals(pct_votes, contest.candidacies.length)
      : ``;


    let embedTypeUsable = 'none';

    if (typeof embedType !== 'undefined') {
      embedTypeUsable = embedType;
    }

    const showcaseClass = cx({
      seat__races: !isShowcaseRace,
      seat__highlight_races: isShowcaseRace,
    });

    const rowClass = cx('seat__race', {
      'seat__race--Democrat': partyName == 'Democrat',
      'seat__race--Republican': partyName == 'Republican',
    });

    const klaxon_sos = 'Race declared by Decision Desk HQ'; // if we switch sources to TX SOS and someone else has declared the winner

    const klaxon = shouldDisplayResult 
      ? source == 'Decision Desk HQ'
        ? alert_winners(contest.contest_result, totalVotes)
        : klaxon_sos
      : alert_conditionals(pct_votes, contest.candidacies.length);

    const checkmark = '\u2713';

    let checkClass = cx('check', {
      'dem-winner':
        contest.party == 'Democrat' && contest.contest_result != 'runoff',
      'rep-winner':
        contest.party == 'Republican' && contest.contest_result != 'runoff',
      'runoff-winner': contest.contest_result == 'runoff',
    });

    const contestMetricsClass = cx('contest__metrics', {
      homepage__metrics: isHomepage,
      'non-homepage__metrics': !isHomepage,
    });

    let hasTurnoutmodel = source == 'Decision Desk HQ' ? true : false;

    const precinctsClass = cx('contest__precincts');

    let ManualoverrideSource;

    // If there is a race, that's being manually updated, you can change the Source to whatever the new votes are coming from here if you uncomment the code below. This is only if the source isn't Texas Secretary of State. If it is, then you don't have to do anything.

    // if (contest.office == 'U.S. House' && contest.office_division == 37 && contest.party == 'Republican') {
    //   ManualoverrideSource = 'Travis County Elections'
    // }
    // else {
    //   ManualoverrideSource = ''
    // }

    ManualoverrideSource = '';

    const IsAnnotations =
      annotations && annotations.length > 0 && annotations[0].value != ''
        ? true
        : false;

    const IsNoted = notes && notes.length > 0 ? true : false;

    return (
      <div class="seat">
        {!isShowcaseRace && embedType == null && (
          <span>
            <h3 class="seat__title">
              <span>{contestTitle}</span>
            </h3>
          </span>
        )}
        <div class={showcaseClass}>
          <div class={rowClass}>
            <div class="race">
              {embedType == 'homepage' && (
                <span>
                  <div class="office__title">{contestTitle}</div>
                </span>
              )}
              {embedType == 'internal' && (
                <span>
                  <h2 class="office__title">{contestTitle}</h2>
                </span>
              )}

              <div class={contestMetricsClass}>
                {isCanvass == false && (
                  <h4 class="klaxon__text">
                    {shouldDisplayResult && source == 'Decision Desk HQ' ? (
                      <span class={checkClass}>{checkmark}</span>
                    ) : (
                      ''
                    )}
                    {klaxon}
                  </h4>
                )}
                {ManualoverrideSource == '' && isCanvass == false && (
                  <p class={precinctsClass}>
                    {source != 'Decision Desk HQ'
                      ? `${intcomma(
                          contest.pollinglocationsreporting
                        )} of ${intcomma(
                          contest.total_pollinglocations
                        )} polling locations reporting (${contest.pollinglocations_reporting_percent.toFixed(
                          1
                        )}%).`
                      : pct_votes_turnout_w_uncontested}{' '}
                    {pct_votes_explain}
                  </p>
                )}
                {
                  (ManualoverrideSource = !'' &&
                    source != 'Texas Secretary of State' &&
                    source != 'Decision Desk HQ' &&
                    isCanvass == false && (
                      <p class={precinctsClass}>
                        {`${intcomma(
                          contest.pollinglocationsreporting
                        )} of ${intcomma(
                          contest.total_pollinglocations
                        )} polling locations reporting (${contest.pollinglocations_reporting_percent.toFixed(
                          1
                        )}%).`}

                        {/* <span class="source">{` Source: ${ManualoverrideSource}`}</span> */}
                      </p>
                    ))
                }
              </div>

              <table class="race__candidates">
                {tableHeader}
                <tbody>
                  {isShowcaseRace &&
                    candidates.map((candidate: any, idx: any) =>
                      renderCandidate(
                        candidate,
                        idx,
                        candidatesRender,
                        viewAllCandidates,
                        { totalVotes }
                      )
                    )}

                  {!isShowcaseRace &&
                    candidates.map((candidate: any, idx: any) =>
                      renderCandidate(candidate, idx, viewAllCandidates, {
                        totalVotes,
                      })
                    )}
                  {candidates.length > CandidatesShown && (
                    <OtherCandidate
                      isViewable={!viewAllCandidates}
                      candidateCount={candidates.length}
                      embedType={embedType}
                      onClick={() => {
                        this.onViewableToggle();
                      }}
                    />
                  )}
                </tbody>
                {candidates.length > CandidatesShown &&
                  viewAllCandidates && (
                    <tr class="candidate race__candidate-row">
                      <td
                        class="candidate__cell candidate__party t-align-center"
                        onClick={() => {
                          this.onViewableToggle();
                        }}
                      >
                        <svg class="icon icon-angle-up">
                          <use xlinkHref="#icon-angle-up" />
                        </svg>
                      </td>
                      <th class="candidate__cell candidate__name">
                        <button
                          class={embedType}
                          onClick={() => {
                            this.onViewableToggle();
                          }}
                        >
                          Show fewer ({CandidatesShown})
                        </button>
                      </th>
                      <th class="candidate__cell candidate__votes"></th>
                      <th class="candidate__cell candidate__pct"></th>
                    </tr>
                  )}
              </table>

              {/* {isHomepage && (
            <div class="dropped_out__div" style="margin-top: 0.625rem;">
              *Dropped out
            </div>
          )} */}
              {IsNoted == true && (
                <p class="note__text">{converter.convert(notes[0].value)}</p>
              )}
              {IsAnnotations == true && (
                <div class="anno-text notes-annotations">
                  <span class="is-sr-only">Note:</span>
                  <svg class="icon note-icon anno-div" aria-hidden="true">
                    <use xlinkHref="#note-icon" />
                  </svg>

                  <p class="annotations t-links-underlined">
                    <span class="font-weight-bold">Noteworthy: </span>
                    {converter.convert(annotations[0].value)}
                  </p>
                </div>
              )}
              <div class="below-races">
              <div class="source">{` Source: ${source}`}</div>
                {(embedType == 'homepage' || embedType == 'internal') && (
                  <div class="countdown">
                    <span class="counter-homepage">LAST UPDATED {asOf}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

Contest.defaultProps = {
  isShowcaseRace: false,
  includeCountyRundown: false,
};

export default Contest;
